import { useUserState } from '../hooks/useUserState';

// Format number with commas
function formatNumber(num: number, decimals: number = 2): string {
  return num.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

export function AccountEquity() {
  const { accountValue, positions, isConnected, balance } = useUserState();

  // Calculate unrealized PnL from all positions
  const unrealizedPnl = positions.reduce((sum, pos) => {
    return sum + parseFloat(pos.unrealizedPnl);
  }, 0);

  // Calculate total margin from all positions
  const totalMargin = positions.reduce((sum, pos) => {
    return sum + parseFloat(pos.marginUsed);
  }, 0);

  // Calculate total position value (for leverage calculation)
  const totalPositionValue = positions.reduce((sum, pos) => {
    const size = Math.abs(parseFloat(pos.szi));
    const entryPrice = parseFloat(pos.entryPx);
    const pnl = parseFloat(pos.unrealizedPnl);
    // Mark price = entry price + (pnl / size)
    const markPrice = size !== 0 ? entryPrice + (pnl / size) : entryPrice;
    return sum + (size * markPrice);
  }, 0);

  // TODO: Get maintenance margin from API (currently using estimate)
  const maintenanceMargin = totalMargin * 0.5; // Rough estimate

  // Calculate account leverage: total cross positions value / cross account value
  const accountLeverage = accountValue > 0 ? totalPositionValue / accountValue : 0;

  if (!isConnected) {
    return (
      <div className="bg-[#0e0f10] border border-[#1a1d20] rounded-lg p-4">
        <div className="text-[13px] text-[#6c7884] text-center">
          Connect wallet to view account equity
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#0e0f10] border border-[#1a1d20] rounded-lg">
      {/* Header */}
      <div className="px-4 py-3 border-b border-[#1a1d20]">
        <h3 className="text-[12px] font-medium text-[#6c7884]">Account Equity</h3>
      </div>

      {/* Content */}
      <div className="p-4 space-y-2.5">
        {/* Account Value */}
        <div className="flex justify-between items-center">
          <span className="text-[12px] text-[#6c7884]">Account Value</span>
          <span className="text-[12px] font-medium text-white">
            ${formatNumber(accountValue, 2)}
          </span>
        </div>

        {/* Perps PnL */}
        <div className="flex justify-between items-center">
          <span className="text-[12px] text-[#6c7884]">Perps PnL</span>
          <span className={`text-[12px] font-medium ${
            unrealizedPnl >= 0 ? 'text-[#0ecb81]' : 'text-[#f6465d]'
          }`}>
            {unrealizedPnl >= 0 ? '+$' : '-$'}{formatNumber(Math.abs(unrealizedPnl), 2)}
          </span>
        </div>

        {/* Divider */}
        <div className="border-t border-[#1a1d20] pt-2.5">
          <div className="text-[10px] font-medium text-[#6c7884] mb-2.5 uppercase tracking-wide">
            Perps Overview
          </div>

          <div className="space-y-2">
            {/* Balance */}
            <div className="flex justify-between items-center">
              <span className="text-[12px] text-[#6c7884]">Balance</span>
              <span className="text-[12px] font-medium text-white">
                ${formatNumber(balance, 2)}
              </span>
            </div>

            {/* Unrealized PnL */}
            <div className="flex justify-between items-center">
              <span className="text-[12px] text-[#6c7884]">Unrealized PnL</span>
              <span className={`text-[12px] font-medium ${
                unrealizedPnl >= 0 ? 'text-[#0ecb81]' : 'text-[#f6465d]'
              }`}>
                {unrealizedPnl >= 0 ? '+$' : '-$'}{formatNumber(Math.abs(unrealizedPnl), 2)}
              </span>
            </div>

            {/* Cross Margin Used */}
            <div className="flex justify-between items-center">
              <span className="text-[12px] text-[#6c7884]">Cross Margin Used</span>
              <span className="text-[12px] font-medium text-white">
                ${formatNumber(totalMargin, 2)}
              </span>
            </div>

            {/* Maintenance Margin */}
            <div className="flex justify-between items-center">
              <span className="text-[12px] text-[#6c7884]">Maintenance Margin</span>
              <span className="text-[12px] font-medium text-white">
                ${formatNumber(maintenanceMargin, 2)}
              </span>
            </div>

            {/* Cross Account Leverage */}
            <div className="flex justify-between items-center">
              <span className="text-[12px] text-[#6c7884]">Cross Account Leverage</span>
              <span className="text-[12px] font-medium text-white">
                {formatNumber(accountLeverage, 1)}x
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
